/**
 * Progression sur l'ensemble des missions d'un scénario.
 * La dernière scène de la dernière mission vaut 100 %.
 * Partagée par le jeu, le script de rattrapage et POST /api/stats/update.
 */

export interface ProgressScene {
  id: string;
  type?: string;
  /** Choice branches, or another option shape the progress walk ignores. */
  options?: unknown;
}

export interface ProgressScenario {
  scenes: ProgressScene[];
  metadata?: {
    chapters?: { startScene?: string; missionId?: string }[];
    missions?: { id: string; startScene?: string }[];
  };
}

export interface ScenarioProgress {
  currentIndex: number;
  totalItems: number;
  percentage: number;
}

function choiceBranches(scene: ProgressScene): ProgressScene[][] {
  if (scene.type !== 'choice' || !Array.isArray(scene.options)) return [];
  const branches: ProgressScene[][] = [];
  for (const option of scene.options) {
    if (!option || typeof option !== 'object' || !('scenes' in option)) continue;
    const nested = option.scenes;
    if (Array.isArray(nested) && nested.length > 0) branches.push(nested as ProgressScene[]);
  }
  return branches;
}

function sceneTreeContains(scenes: ProgressScene[], sceneId: string): boolean {
  for (const scene of scenes) {
    if (scene.id === sceneId) return true;
    for (const branch of choiceBranches(scene)) {
      if (sceneTreeContains(branch, sceneId)) return true;
    }
  }
  return false;
}

/** Top-level scene that owns `sceneId`, including when it sits inside a choice branch. */
function resolveRootSceneId(scenes: ProgressScene[], sceneId: string): string | null {
  for (const scene of scenes) {
    if (scene.id === sceneId) return scene.id;
    for (const branch of choiceBranches(scene)) {
      if (sceneTreeContains(branch, sceneId)) return scene.id;
    }
  }
  return null;
}

function missionSceneIds(scenario: ProgressScenario): string[] {
  const scenes = scenario.scenes;
  const indexById = new Map(scenes.map((scene, index) => [scene.id, index]));
  const missionIds = new Set((scenario.metadata?.missions ?? []).map((mission) => mission.id));

  const chapters = (scenario.metadata?.chapters ?? [])
    .filter((chapter) => chapter.startScene && indexById.has(chapter.startScene))
    .map((chapter) => ({
      missionId: chapter.missionId,
      startIndex: indexById.get(chapter.startScene!)!,
    }))
    .sort((a, b) => a.startIndex - b.startIndex);

  const missionChapters = chapters.filter(
    (chapter) => chapter.missionId && missionIds.has(chapter.missionId),
  );

  if (missionChapters.length === 0) {
    return scenes.map((scene) => scene.id);
  }

  const start = missionChapters[0]!.startIndex;
  const lastStart = missionChapters[missionChapters.length - 1]!.startIndex;
  const nextOutside = chapters.find(
    (chapter) =>
      chapter.startIndex > lastStart && !(chapter.missionId && missionIds.has(chapter.missionId)),
  );
  const end = nextOutside ? nextOutside.startIndex : scenes.length;
  return scenes.slice(start, end).map((scene) => scene.id);
}

/**
 * Position of `sceneId` across every mission in the scenario.
 * Returns null when the scene id does not exist in the scenario.
 */
export function calculateScenarioProgress(
  scenario: ProgressScenario,
  sceneId: string,
): ScenarioProgress | null {
  if (!sceneId) return null;

  const rootId = resolveRootSceneId(scenario.scenes, sceneId);
  if (!rootId) return null;

  const ids = missionSceneIds(scenario);
  const totalItems = ids.length;
  if (totalItems === 0) {
    return { currentIndex: 0, totalItems: 0, percentage: 0 };
  }

  const index = ids.indexOf(rootId);
  if (index >= 0) {
    const percentage = Math.min(100, Math.max(0, Math.round(((index + 1) / totalItems) * 100)));
    return { currentIndex: index, totalItems, percentage };
  }

  const rootIndex = scenario.scenes.findIndex((scene) => scene.id === rootId);
  const firstIndex = scenario.scenes.findIndex((scene) => scene.id === ids[0]);
  if (rootIndex < firstIndex) {
    return { currentIndex: 0, totalItems, percentage: 0 };
  }
  return { currentIndex: totalItems - 1, totalItems, percentage: 100 };
}

/**
 * Percentage reached at the last scene of `missionId`.
 * The mission starts at its last chapter (or `mission.startScene` when it has no
 * chapter) and ends right before the next chapter or mission start.
 * Returns null when the mission cannot be placed in the scenario.
 */
export function calculateMissionEndProgress(
  scenario: ProgressScenario,
  missionId: string,
): number | null {
  const scenes = scenario.scenes;
  if (!missionId || scenes.length === 0) return null;

  const indexById = new Map(scenes.map((scene, index) => [scene.id, index]));
  const indexOf = (sceneId: string | undefined): number | undefined =>
    sceneId ? indexById.get(sceneId) : undefined;

  const chapters = scenario.metadata?.chapters ?? [];
  const missions = scenario.metadata?.missions ?? [];

  const chapterStarts = chapters
    .filter((chapter) => chapter.missionId === missionId)
    .map((chapter) => indexOf(chapter.startScene))
    .filter((index): index is number => index !== undefined);
  if (chapterStarts.length === 0) {
    const fallback = indexOf(missions.find((mission) => mission.id === missionId)?.startScene);
    if (fallback !== undefined) chapterStarts.push(fallback);
  }
  if (chapterStarts.length === 0) return null;

  const lastStart = Math.max(...chapterStarts);
  const boundaries = [
    ...chapters.map((chapter) => indexOf(chapter.startScene)),
    ...missions.map((mission) => indexOf(mission.startScene)),
  ].filter((index): index is number => index !== undefined && index > lastStart);

  const endIndex = boundaries.length > 0 ? Math.min(...boundaries) - 1 : scenes.length - 1;
  return calculateScenarioProgress(scenario, scenes[endIndex]!.id)?.percentage ?? null;
}

/** Stored progress never goes down; an unplaceable scene keeps the stored value. */
export function mergeProgressPercent(existing: number, computed: number | null): number {
  const current = Number.isFinite(existing) ? existing : 0;
  const merged = computed === null ? current : Math.max(current, computed);
  return Math.min(100, Math.max(0, merged));
}
